import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useEditNotificationReadStateMutation } from 'store/services/core/notification';
import { useGetUserAgendaNotificationsQuery } from 'store/services/core/userNotifications';

const NOTIFICATIONS_FILTER_PARAM = 'notifications';

export const buildAgendaNotificationsPath = (path: string) => {
  const separator = path.includes('?') ? '&' : '?';

  return `${path}${separator}${NOTIFICATIONS_FILTER_PARAM}=1`;
};

type UseAgendaNotificationsFilterOptions = {
  agendaIds: string[];
};

export const useAgendaNotificationsFilter = ({ agendaIds }: UseAgendaNotificationsFilterOptions) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [editNotificationReadState] = useEditNotificationReadStateMutation();
  const markedAsReadRef = useRef(false);

  const isNotificationsFilterActive = searchParams.get(NOTIFICATIONS_FILTER_PARAM) === '1';
  const skip = agendaIds.length === 0 || markedAsReadRef.current;
  const agendaIdsKey = agendaIds.join(',');

  const { data: infoNotifications = [] } = useGetUserAgendaNotificationsQuery({ actionRequired: false, agendaIds }, { skip });
  const { data: actionNotifications = [] } = useGetUserAgendaNotificationsQuery({ actionRequired: true, agendaIds }, { skip });

  const notificationRecordIds = useMemo(() => {
    const ids = new Set<string>();
    [...infoNotifications, ...actionNotifications].forEach((notification) => {
      if (notification.recordId) {
        ids.add(notification.recordId);
      }
    });
    return [...ids];
  }, [infoNotifications, actionNotifications]);

  const unreadNotificationsCount = infoNotifications.length + actionNotifications.length;

  const activateNotificationsFilter = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set(NOTIFICATIONS_FILTER_PARAM, '1');
      return next;
    });
  }, [setSearchParams]);

  const clearNotificationsFilter = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete(NOTIFICATIONS_FILTER_PARAM);
      return next;
    });
  }, [setSearchParams]);

  useEffect(() => {
    markedAsReadRef.current = false;
  }, [agendaIdsKey, isNotificationsFilterActive]);

  useEffect(() => {
    if (!isNotificationsFilterActive || markedAsReadRef.current || !infoNotifications.length) return;

    const notificationIds = infoNotifications.map((notification) => notification.id);

    if (notificationIds.length === 0) return;

    markedAsReadRef.current = true;

    const markAsRead = async () => {
      try {
        await editNotificationReadState({
          userNotificationsIds: notificationIds,
          state: 'Read',
        }).unwrap();
      } catch {
        return;
      }

      window.dispatchEvent(new CustomEvent('flowio:refresh-notifications'));
    };

    markAsRead();
  }, [editNotificationReadState, infoNotifications, isNotificationsFilterActive]);

  return {
    isNotificationsFilterActive,
    notificationRecordIds,
    unreadNotificationsCount,
    activateNotificationsFilter,
    clearNotificationsFilter,
  };
};
